import { Link } from 'react-router-dom';
import type { AuditDetail, QualificationSection } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { AutoSaveIndicator } from '../../components/common/AutoSaveIndicator';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, ClipboardCheck, Pencil, RotateCw } from 'lucide-react';
import type { AutoSaveStatus } from '../../hooks/useAutoSave';
import { unsavedChangesPrompt } from '../../hooks/useUnsavedChangesGuard';
import { AUDIT_ARCHIVED, AUDIT_STATUS } from '../audits/auditStatus';
import AuditArchiveMenu from './AuditArchiveMenu';

export interface AuditStats {
  answered: number;
  total: number;
  findings: number;
  actions: number;
}

interface AuditProgressBarProps {
  audit: AuditDetail;
  sections: QualificationSection[];
  stats: AuditStats;
  canEdit: boolean;
  saveStatus: AutoSaveStatus;
  lastSavedAt: Date | null;
  hasUnsaved: boolean;
  /** Why the last autosave failed, shown beside Retry. */
  saveError?: string | null;
  /** Answers not yet saved — leaving by the back link asks first. */
  unsavedCount: number;
  onRetrySave: () => void;
  onJump: (sectionId: number) => void;
  onReview: () => void;
  /** Opens Edit details (name, date, location, lead auditor). */
  onEditDetails: () => void;
  /** Show Edit details: in progress (customer), or completed (approver, corrections). */
  canEditDetails: boolean;
  /** Archive / restore / delete (shown when `audit.can_archive`); each resolves null on success, else the reason. */
  onArchive: () => Promise<string | null>;
  onRestore: () => Promise<string | null>;
  onDelete: () => Promise<string | null>;
}

/**
 * Sticky audit header: who / what / when, live progress, autosave state,
 * a section jump, Review & complete, and (customer admins) the More menu.
 */
export default function AuditProgressBar({
  audit, sections, stats, canEdit, saveStatus, lastSavedAt, hasUnsaved, unsavedCount, saveError = null, onRetrySave, onJump, onReview,
  onEditDetails, canEditDetails, onArchive, onRestore, onDelete,
}: AuditProgressBarProps) {
  const { formatDate } = useFormatters();
  const status = AUDIT_STATUS[audit.status];
  const percent = stats.total ? Math.round((stats.answered * 100) / stats.total) : 0;

  return (
    <div className="sticky top-16 z-20 -mx-4 border-b bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:-mx-6 md:px-6 lg:-mx-8 lg:px-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <Link to="/questionnaires?tab=audits" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              const pending = saveStatus === 'error' ? Math.max(unsavedCount, 1) : unsavedCount;
              if (pending > 0 && !window.confirm(unsavedChangesPrompt(pending))) e.preventDefault();
            }}>
            <ArrowLeft className="h-4 w-4" />Audits
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="flex min-w-0 items-center gap-2 text-xl font-semibold">
              <ClipboardCheck className="h-5 w-5 shrink-0 text-muted-foreground" />
              <span className="truncate">{audit.supplier_name}</span>
            </h1>
            <Badge className={`${status.className} border-0`}>{status.label}</Badge>
            {audit.archived && <Badge className={`${AUDIT_ARCHIVED.className} border-0`}>{AUDIT_ARCHIVED.label}</Badge>}
          </div>
          {audit.name && <p className="text-base font-medium" data-testid="audit-name">{audit.name}</p>}
          <p className="text-sm text-muted-foreground">
            {audit.template_name} · Lead auditor {audit.lead_auditor_name || '—'} · {formatDate(audit.audit_date)}
            {audit.location ? ` · ${audit.location}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && (
            <>
              <AutoSaveIndicator status={saveStatus} lastSavedAt={lastSavedAt} hasUnsavedChanges={hasUnsaved && saveStatus !== 'error'}
                saveButtonLabel="Retry" className="text-sm" />
              {saveStatus === 'error' && (
                <>
                  {saveError && <span role="alert" className="max-w-xs text-sm text-amber-700 dark:text-amber-400">{saveError}</span>}
                  <Button variant="outline" onClick={onRetrySave}><RotateCw className="h-4 w-4 mr-1.5" />Retry</Button>
                </>
              )}
            </>
          )}
          {sections.length > 1 && (
            <Select onValueChange={(v) => onJump(Number(v))}>
              <SelectTrigger className="h-11 w-64 text-base" aria-label="Jump to section"><SelectValue placeholder="Jump to section" /></SelectTrigger>
              <SelectContent className="max-h-[60vh]">
                {sections.map((s) => <SelectItem key={s.id} value={String(s.id)} className="py-3 text-base">{s.title}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {canEditDetails && (
            <Button size="lg" variant="outline" onClick={onEditDetails}>
              <Pencil className="h-4 w-4 mr-1.5" />Edit details
            </Button>
          )}
          {canEdit && <Button size="lg" onClick={onReview}>Review &amp; complete</Button>}
          {audit.can_archive && (
            <AuditArchiveMenu audit={audit} onArchive={onArchive} onRestore={onRestore} onDelete={onDelete} />
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Progress value={percent} className="h-2 max-w-xs" aria-label={`${percent}% answered`} />
        <span className="text-sm text-muted-foreground" data-testid="audit-progress">
          {stats.answered} of {stats.total} answered · {stats.findings} {stats.findings === 1 ? 'finding' : 'findings'}
          {' '}· {stats.actions} {stats.actions === 1 ? 'action' : 'actions'}
        </span>
      </div>
    </div>
  );
}
